/**
 * @file This file contains the definition and implementation of the QuestionnaireStore.
 * The store is used to manage the errors of the questionnaire components. It communicates between {@link QuestionnaireContainer} and all the EhsFormioComponents to show the error messages in them.
 * That way, we can use our own EhsReact components error handling and show the error messages making the questionnaire more accessible.
 *
 */
import { createStore } from './Store';
import type { Component } from '@formio/react';
import _ from 'lodash';

/**
 * Represents error messages related to a questionnaire component.
 */
export type QuestionnaireErrorMessages = {
  /**
   * The error message describing the issue.
   */
  message: string;

  /**
   * Indicates whether the error originated from an external source.
   */
  external: boolean;

  /**
   * The formatted key or path that identifies the specific field or component associated with the error.
   */
  formattedKeyOrPath: string;

  /**
   * The component associated with the error.
   */
  component: Component<any>;
};

/**
 * Interface representing the store for managing questionnaire errors.
 */
export interface QuestionnaireStore {
  /**
   * Indicates if we are working with Questionnaire or Stroke's Formio.io.
   */
  formType?: 'questionnaire' | 'stroke';
  /**
   * A record of error messages, keyed by component identifiers.
   */
  errors: Record<string, QuestionnaireErrorMessages>;

  /**
   * Flag indicating whether the error alert should be shown.
   */
  shouldShowErrorAlert: boolean;

  /**
   * Indicates whether the form contains different data since being loaded.
   */
  isFormDataModified: boolean;

  /**
   * Updates the form state based on the current form data.
   * @param formData
   */
  setFormState(formData: any): void;

  /**
   * The form data contained upon initial load.
   */
  initialFormData: any;

  /**
   * Set the form data contained upon initial load.
   * @param formData
   */
  setInitialFormData(formData: any): void;

  /**
   * Retrieves the error messages for a specific component.
   *
   * @param cmp - The identifier of the component. If not provided, returns null.
   * @returns The error messages for the specified component, or null if no component identifier is provided.
   */
  getError(cmp?: string): QuestionnaireErrorMessages | null;

  /**
   * Sets the error messages for a specific component and sorts the components based on the provided sorted component IDs.
   *
   * @param cmp - The identifier of the component.
   * @param value - The error messages to set for the specified component.
   * @param sortedComponentsIds - The sorted list of component identifiers.
   */
  setErrorWithComponentSorting(
    cmp: string,
    value: QuestionnaireErrorMessages,
    sortedComponentsIds: string[]
  ): void;

  /**
   * Sets multiple error messages at once.
   *
   * @param errors - A record of error messages keyed by component identifiers.
   */
  setErrors(errors: Record<string, QuestionnaireErrorMessages>): void;

  /**
   * Sets the store to work with the questionnaire or stroke.
   */
  setFormType(formType: 'questionnaire' | 'stroke'): void;

  /**
   * Resets all error messages.
   */
  resetErrors(): void;

  /**
   * Preview language translations to apply inside Formio custom components.
   */
  previewTranslations?: Record<string, string>;
  setPreviewTranslations(t: Record<string, string> | undefined): void;

  /**
   * Preview locale used for locale-sensitive formatting in Formio preview.
   */
  previewLocale?: string;
  setPreviewLocale(locale: string | undefined): void;
}

export const [
  useQuestionnaireStore,
  QuestionnaireStoreApi
] = createStore<QuestionnaireStore>('QuestionnaireStore', (set, get) => ({
  formType: undefined,
  errors: {},
  shouldShowErrorAlert: false,
  initialFormData: undefined,
  isFormDataModified: false,
  getError(cmp?: string): QuestionnaireErrorMessages | null {
    if (!cmp) {
      return null;
    }
    return get().errors[cmp] ?? null;
  },
  setFormState(formData: any): void {
    set(
      state =>
        (state.isFormDataModified = !_.isEqual(get().initialFormData, formData))
    );
  },
  setInitialFormData(formData: any): void {
    set(state => {
      state.initialFormData = _.cloneDeep(formData);
      state.isFormDataModified = false;
    });
  },
  setErrorWithComponentSorting(
    cmp: string,
    value: QuestionnaireErrorMessages,
    sortedComponentsIds: string[]
  ): void {
    const errors = { ...get().errors };
    const sortedErrors = sortedComponentsIds.reduce(
      (acc: Record<string, QuestionnaireErrorMessages>, id: string) => {
        if (id in errors) {
          acc[id] = errors[id];
        }
        if (id === cmp) {
          acc[id] = value;
        }
        return acc;
      },
      {} as Record<string, QuestionnaireErrorMessages>
    );

    // only show error alert after submitting and not after one component change
    const shouldShowErrorAlert = get().shouldShowErrorAlert
      ? Object.values(sortedErrors).filter(e => e).length > 0
      : false;

    set(state => {
      state.errors = sortedErrors;
      state.shouldShowErrorAlert = shouldShowErrorAlert;
    });
  },
  setErrors(errors: Record<string, QuestionnaireErrorMessages>): void {
    set(state => {
      state.errors = errors;
      state.shouldShowErrorAlert = true;
    });
  },
  setFormType(formType: 'questionnaire' | 'stroke') {
    set(state => {
      state.formType = formType;
    });
  },
  resetErrors() {
    set(state => {
      state.errors = {};
      state.shouldShowErrorAlert = false;
    });
  },
  previewTranslations: undefined,
  setPreviewTranslations(t: Record<string, string> | undefined) {
    set(state => {
      state.previewTranslations = t;
    });
  },
  previewLocale: undefined,
  setPreviewLocale(locale: string | undefined) {
    set(state => {
      state.previewLocale = locale;
    });
  }
}));
